import React, { useState } from "react"
import { useMutation } from "@apollo/client"
import { Button } from "reactstrap"

import ExtractedFieldRow, { CopyButton } from "./ExtractedFieldRow"
import { ExtractIncidentDocumentDataMutation } from "./queries"
import { POLICE_REPORT_FIELDS, POLICE_REPORT_CONTEXT, groupExtractedFields, asCopyText } from "./policeReportFields"

const SparkleIcon = ({ size = 28 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
        <path d="M18 15l.8 2.2L21 18l-2.2.8L18 21l-.8-2.2L15 18l2.2-.8z" />
    </svg>
)

const AIAnalysisPanel = ({ documentId }) => {
    const [result, setResult] = useState(null)
    const [errorMessage, setErrorMessage] = useState(null)
    const [showRawText, setShowRawText] = useState(false)
    const [runExtraction, { loading }] = useMutation(ExtractIncidentDocumentDataMutation)

    const analyze = async () => {
        setErrorMessage(null)
        setResult(null)
        try {
            const { data } = await runExtraction({
                variables: {
                    input: {
                        documentId,
                        fieldsToExtract: POLICE_REPORT_FIELDS,
                        additionalContext: POLICE_REPORT_CONTEXT,
                    },
                },
            })
            const payload = data && data.extractIncidentDocumentData
            if (!payload || !payload.ok) {
                const message = payload && payload.errors && payload.errors[0] && payload.errors[0].messages[0]
                setErrorMessage(message || "The extraction service returned no result.")
                return
            }
            setResult(payload)
        } catch (e) {
            setErrorMessage(e.message)
        }
    }

    if (loading) {
        return (
            <div className="idm-panel-body idm-panel-centered">
                <div className="idm-progress"><div className="idm-progress-bar" /></div>
                <p className="idm-panel-title">Reading the document&hellip;</p>
                <p className="idm-panel-note">This usually takes 20&ndash;60 seconds.</p>
            </div>
        )
    }

    if (errorMessage) {
        return (
            <div className="idm-panel-body idm-panel-centered">
                <div className="idm-panel-icon idm-panel-icon-danger">
                    <svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.3 4.3L2.5 18a1.7 1.7 0 0 0 1.5 2.5h16a1.7 1.7 0 0 0 1.5-2.5L13.7 4.3a1.7 1.7 0 0 0-3 0z" />
                        <path d="M12 9v4" /><path d="M12 17h.01" />
                    </svg>
                </div>
                <p className="idm-panel-title">Couldn&rsquo;t analyze this document</p>
                <p className="idm-panel-note">Nothing was changed.</p>
                <div className="idm-error-detail"><code>{errorMessage}</code></div>
                <Button className="idm-btn-primary" onClick={analyze}>Try again</Button>
            </div>
        )
    }

    if (!result) {
        return (
            <div className="idm-panel-body idm-panel-centered">
                <div className="idm-panel-icon"><SparkleIcon /></div>
                <p className="idm-panel-title">Analyze this document</p>
                <p className="idm-panel-lead">
                    Reads the report and pulls out the incident details, vehicles, people, citations and narrative
                    so you can copy them straight across.
                </p>
                <Button className="idm-btn-primary" onClick={analyze}>
                    <SparkleIcon size={15} />&nbsp;Run analysis
                </Button>
                <p className="idm-panel-note">Usually takes 20&ndash;60 seconds.</p>
                <p className="idm-panel-note idm-panel-note-quiet">Nothing is written to the incident &mdash; read only.</p>
            </div>
        )
    }

    const groups = groupExtractedFields(result.structuredOutput)
    const fieldCount = groups.reduce((total, group) => total + group.rows.length, 0)

    return (
        <div className="idm-panel-body">
            <div className="idm-result-bar">
                <div className="idm-result-status">
                    <span className="idm-result-done">Analyzed</span>
                    <span className="idm-result-meta">{fieldCount} field{fieldCount === 1 ? "" : "s"} found</span>
                </div>
                <div className="idm-result-actions">
                    <Button className="idm-btn-primary idm-btn-small" onClick={() => {
                        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(asCopyText(groups))
                    }}>Copy all</Button>
                    <button type="button" className="idm-copy-chip" title="Re-run analysis" aria-label="Re-run analysis" onClick={analyze}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="idm-result-scroll">
                {groups.length === 0 && (
                    <div className="idm-warning-box">
                        <p className="idm-warning-title">No structured fields came back</p>
                        <p className="idm-warning-text">The document was read, but the fields couldn&rsquo;t be pulled out of it. The raw text is below.</p>
                    </div>
                )}

                {groups.map((group) => (
                    <div key={group.key}>
                        <div className="idm-group-header">
                            <div className="idm-group-heading">
                                <span className="idm-group-title">{group.label.toUpperCase()}</span>
                                <span className="idm-group-count">{group.rows.length}</span>
                            </div>
                            <CopyButton
                                text={group.rows.map((r) => `${r.label}: ${r.value}`).join("\n")}
                                label={`Copy ${group.label}`}
                            />
                        </div>
                        {group.rows.map((row) => <ExtractedFieldRow key={row.key} row={row} />)}
                    </div>
                ))}

                {!!result.text && (
                    <div className="idm-raw-text">
                        <button type="button" className="idm-disclosure" onClick={() => setShowRawText(!showRawText)}>
                            <span>Raw OCR text</span>
                            <span className="idm-disclosure-caret">{showRawText ? "−" : "+"}</span>
                        </button>
                        {showRawText && (
                            <>
                                <div className="idm-raw-text-actions"><CopyButton text={result.text} label="Copy raw text" /></div>
                                <pre className="idm-raw-text-body">{result.text}</pre>
                            </>
                        )}
                    </div>
                )}

                <p className="idm-panel-note idm-panel-note-quiet idm-result-footnote">
                    Read only &mdash; nothing here is saved to the incident.
                </p>
            </div>
        </div>
    )
}

export default AIAnalysisPanel
